import {Session, Points, Location, PointsAccrual, User} from "../../types/Types";
import {POINTS_CONFIG} from "./pointsConfig";

export interface UserPoints { userId: string, points: number }
export interface SessionRank { user: User, points: number }

export function getDateFromTimeStamp(ts: any): Date {
  if (!ts) return new Date('Invalid');
  if (ts instanceof Date) return ts;
  if (ts.toDate) return ts.toDate(); // Firestore Timestamp
  if (typeof ts === "number") return new Date(ts);
  if (typeof ts === "string") return new Date(ts);
  return new Date('Invalid');
}

export function formatDate(date: Date): string {
  return date.toISOString().slice(0, 10);
}

export function filterSessionsPerDayPerLocation(sessions: Session[]): Session[] {
  const lastCheckInByUserAndLocation = new Map<string, Date>();
  const sessionsOutsideCooldown = sessions
    .slice()
    .sort((a, b) => getDateFromTimeStamp(a.checkInTime).getTime() - getDateFromTimeStamp(b.checkInTime).getTime())
    .filter(session => {
      const checkIn = getDateFromTimeStamp(session.checkInTime);
      if (Number.isNaN(checkIn.getTime())) {
        return true;
      }

      const userLocationKey = `${session.userId}:${session.locationId}`;
      const lastCheckIn = lastCheckInByUserAndLocation.get(userLocationKey);
      lastCheckInByUserAndLocation.set(userLocationKey, checkIn);

      if (!lastCheckIn) {
        return true;
      }

      const minutesSinceLastCheckIn = (checkIn.getTime() - lastCheckIn.getTime()) / (1000 * 60);
      return minutesSinceLastCheckIn >= 60;
    });

  const map = new Map<string, Session[]>();
  sessionsOutsideCooldown.forEach(session => {
    const dayString = formatDate(getDateFromTimeStamp(session.checkInTime));
    const locationId = session.locationId;
    const key = `${session.userId}:${dayString}:${locationId}`;
    if (!map.has(key)) {
      map.set(key, []);
    }
    const arr = map.get(key)!;
    if (arr.length < POINTS_CONFIG.MAX_CHECKINS_PER_DAY_PER_LOCATION) {
      arr.push(session);
    }
  });
  return ([] as Session[]).concat(...Array.from(map.values()));
}

function getLocationName(locations: Location[] | undefined, locationId: string): string {
  if (!locations) return locationId;
  const matches = locations.filter(function (loc) {
    return loc.id === locationId;
  });
  return matches.length > 0 ? matches[0].name : locationId;
}

function getLocation(locations: Location[] | undefined, locationId: string): Location | undefined {
  if (!locations) return undefined;
  return locations.find(loc => loc.id === locationId);
}

function isSpainLocation(locations: Location[] | undefined, locationId: string): boolean {
  const location = getLocation(locations, locationId);
  return location?.country === 'Spain' || location?.country === 'ESP';
}

export function calculatePointsAccruals(
    sessions: Session[],
    bonusPoints: Points[],
    locations: Location[] | undefined
): PointsAccrual[] {
  const visitedLocations = new Set<string>();
  const spainBonusAwarded = new Set<string>();

  const sessionAccruals: PointsAccrual[] = sessions.map(session => {
    const checkIn = getDateFromTimeStamp(session.checkInTime);
    const checkOut = getDateFromTimeStamp(session.checkOutTime);
    const durationMinutes = Math.floor(Math.max(0, (checkOut.getTime() - checkIn.getTime()) / (1000 * 60)));

    const visitKey = `${session.userId}:${session.locationId}`;
    const isFirstVisit = !visitedLocations.has(visitKey);
    if (isFirstVisit) {
      visitedLocations.add(visitKey);
    }

    return {
      locationId: session.locationId,
      locationName: session.locationName || locations !== undefined ? getLocationName(locations, session.locationId) : "",
      points: isFirstVisit ? POINTS_CONFIG.FIRST_VISIT_POINTS : Math.floor((durationMinutes / 60) * POINTS_CONFIG.POINTS_PER_HOUR),
      isUnique: isFirstVisit,
      isBonus: false,
      recordedAt: checkIn,
      timeCheckedIn: durationMinutes,
      userId: session.userId
    } as PointsAccrual;
  });

  // Add Spain bonus points for first visit to Spain locations
  const spainBonusAccruals: PointsAccrual[] = sessions
    .filter(session => {
      const visitKey = `${session.userId}:${session.locationId}`;
      if (spainBonusAwarded.has(visitKey)) {
        return false;
      }
      if (isSpainLocation(locations, session.locationId)) {
        spainBonusAwarded.add(visitKey);
        return true;
      }
      return false;
    })
    .map(session => ({
      locationId: session.locationId,
      locationName: session.locationName || locations !== undefined ? getLocationName(locations, session.locationId) : "",
      points: POINTS_CONFIG.BONUS_POINTS_SPAIN,
      isUnique: false,
      isBonus: true,
      recordedAt: getDateFromTimeStamp(session.checkInTime),
      timeCheckedIn: undefined,
      userId: session.userId
    } as PointsAccrual));

  const bonusAccruals: PointsAccrual[] = bonusPoints
      .map(bonus => ({
        locationId: bonus.locationId,
        locationName: locations !== undefined ? getLocationName(locations, bonus.locationId) : "",
        points: POINTS_CONFIG.BONUS_POINTS,
        isUnique: false,
        isBonus: true,
        recordedAt: getDateFromTimeStamp(bonus.recordedAt),
        timeCheckedIn: undefined,
        userId: bonus.userId
      } as PointsAccrual));

  const allAccruals = [...sessionAccruals, ...spainBonusAccruals, ...bonusAccruals]
      .sort((a, b) => b.recordedAt.getTime() - a.recordedAt.getTime());

  return allAccruals;
}

export function calculateTotalPoints(pointsAccruals: PointsAccrual[]): number {
  return pointsAccruals.reduce((sum, accrual) => sum + accrual.points, 0);
}

export function calculateTotalUserPoints(pointsAccruals: PointsAccrual[]): Array<UserPoints> {
  const totals: { [userId: string]: number } = {};

  pointsAccruals.forEach(accrual => {
    if (!totals[accrual.userId]) {
      totals[accrual.userId] = 0;
    }
    totals[accrual.userId] += accrual.points;
  });

  return Object.keys(totals).map(userId => ({
    userId: userId,
    points: totals[userId],
  } as UserPoints));
}

export function getSortedUsers(rankedUsers: Array<SessionRank>) {
  return rankedUsers
      .filter((rankedUser) => {return rankedUser.user && !rankedUser.user.socialDisabled})
      .sort((countA, countB) => {
        if(countA.points > countB.points) {
          return -1;
        } else if(countA.points === countB.points)  {
          return 0;
        } else {
          return 1;
        }
      });
}
